import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  DollarSign, 
  Users, 
  Building2, 
  UserCheck,
  TrendingUp,
  Briefcase,
  UserPlus,
  FileText,
  Settings,
  Calendar,
  AlertTriangle,
  Clock,
  Cake,
  XCircle,
  Gift,
  Download
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    totalActive: 0,
    totalByCompany: [],
    countByRegime: [],
    totalActiveCount: 0,
    headCountData: [],
    employeesByCompany: [],
    turnoverData: []
  });
  const [vacationAlerts, setVacationAlerts] = useState([]);
  const [birthdayEmployees, setBirthdayEmployees] = useState([]);
  const [birthdaysLoading, setBirthdaysLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const lastCheckedMonthRef = useRef(new Date().getMonth());

  useEffect(() => {
    fetchDashboardData();
    fetchVacationAlerts();
    fetchBirthdayEmployees();
  }, []);

  // Reconfere o mês periodicamente para atualizar o card de aniversários
  // automaticamente quando o mês virar, sem precisar recarregar a página.
  useEffect(() => {
    const checkMonthChange = () => {
      const currentMonth = new Date().getMonth();
      if (currentMonth !== lastCheckedMonthRef.current) {
        lastCheckedMonthRef.current = currentMonth;
        fetchBirthdayEmployees();
      }
    };
    const intervalId = setInterval(checkMonthChange, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [
        totalActive, 
        totalByCompany, 
        countByRegime, 
        totalActiveCount, 
        headCount,
        employeesByCompany,
        turnoverData
      ] = await Promise.all([
        axios.get(`${API}/dashboard/total-active`),
        axios.get(`${API}/dashboard/total-by-company`),
        axios.get(`${API}/dashboard/count-by-regime`),
        axios.get(`${API}/dashboard/total-active-count`),
        axios.get(`${API}/headcount`),
        axios.get(`${API}/dashboard/employees-by-company`),
        axios.get(`${API}/dashboard/turnover`)
      ]);

      setDashboardData({
        totalActive: totalActive.data.total,
        totalByCompany: totalByCompany.data,
        countByRegime: countByRegime.data,
        totalActiveCount: totalActiveCount.data.count,
        headCountData: headCount.data,
        employeesByCompany: employeesByCompany.data,
        turnoverData: turnoverData.data
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVacationAlerts = async () => {
    try {
      const response = await axios.get(`${API}/vacations/alerts`);
      setVacationAlerts(response.data);
    } catch (error) {
      console.error('Erro ao carregar alertas de férias:', error);
      // Não mostrar toast de erro pois é opcional
    }
  };

  const fetchBirthdayEmployees = async () => {
    try {
      setBirthdaysLoading(true);
      // Rota enxuta (só ativos, só id/nome/foto/data_nascimento) — evita
      // buscar o cadastro completo de todos os funcionários só pra filtrar aniversário.
      const response = await axios.get(`${API}/employees/aniversariantes`);
      const employees = response.data;
      
      console.log('Employees loaded for birthday check:', employees.length);
      
      // Filter employees with birthdays this month
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      
      console.log('Current month:', currentMonth);
      
      // Parses using local date components so YYYY-MM-DD strings (parsed as UTC
      // midnight by `new Date()`) don't shift a day earlier in negative UTC offsets.
      const parseBirthDate = (value) => {
        if (typeof value === 'string') {
          if (value.includes('/')) {
            // DD/MM/YYYY format
            const [day, month, year] = value.split('/');
            return new Date(year, month - 1, day);
          }
          const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            return new Date(Number(year), Number(month) - 1, Number(day));
          }
          return new Date(value);
        }
        return new Date(value);
      };

      const birthdayThisMonth = employees.filter(employee => {
        if (!employee.data_nascimento) {
          return false;
        }

        try {
          const birthDate = parseBirthDate(employee.data_nascimento);
          const employeeBirthMonth = birthDate.getMonth() + 1;
          console.log(`Employee ${employee.nome}: birth month ${employeeBirthMonth}, current month ${currentMonth}`);

          return employeeBirthMonth === currentMonth;
        } catch (e) {
          console.error(`Error parsing birth date for ${employee.nome}:`, employee.data_nascimento, e);
          return false;
        }
      }).map(employee => {
        let birthDate;
        try {
          birthDate = parseBirthDate(employee.data_nascimento);
        } catch (e) {
          birthDate = new Date();
        }

        return {
          ...employee,
          birthday_date: birthDate,
          age: currentYear - birthDate.getFullYear()
        };
      }).sort((a, b) => a.birthday_date.getDate() - b.birthday_date.getDate());
      
      console.log('Birthday employees found:', birthdayThisMonth.length);
      setBirthdayEmployees(birthdayThisMonth);
    } catch (error) {
      console.error('Erro ao carregar aniversários:', error);
      // Fallback to regular employees endpoint
      try {
        const response = await axios.get(`${API}/employees`);
        const employees = response.data;
        setBirthdayEmployees([]); // Set empty for now, will fix data structure issue
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        setBirthdayEmployees([]);
      }
    } finally {
      setBirthdaysLoading(false);
    }
  };

  const exportBirthdaysCSV = () => {
    if (birthdayEmployees.length === 0) return;

    const headers = ['Nome', 'Data de Nascimento', 'Idade'];
    const rows = birthdayEmployees.map((employee) => [
      employee.nome,
      employee.birthday_date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      employee.age
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const monthLabel = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).replace(/\s+de\s+/g, '_');
    link.href = url;
    link.setAttribute('download', `aniversariantes_${monthLabel}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getRegimeCount = (regime) => {
    const item = dashboardData.countByRegime.find(r => r.regime === regime);
    return item ? item.count : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-600 mt-1">Visão geral do GridProfile</p>
        </div>
      </div>

      {/* Main metrics cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total geral de funcionários ativos */}
        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Total Funcionários Ativos
            </CardTitle>
            <UserCheck className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {dashboardData.totalActiveCount}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Funcionários com situação ativa
            </p>
          </CardContent>
        </Card>

        {/* Total valor folha - funcionários ativos */}
        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Total Folha de Pagamento
            </CardTitle>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {formatCurrency(dashboardData.totalActive)}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Soma total de funcionários ativos
            </p>
          </CardContent>
        </Card>

        {/* Funcionários PJ */}
        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Funcionários PJ
            </CardTitle>
            <Briefcase className="h-5 w-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {getRegimeCount('PJ')}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Regime Pessoa Jurídica
            </p>
          </CardContent>
        </Card>

        {/* Funcionários CLT */}
        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Funcionários CLT
            </CardTitle>
            <Users className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {getRegimeCount('CLT')}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Regime CLT
            </p>
          </CardContent>
        </Card>

        {/* Vacation Alerts Summary */}
        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Férias Vencidas
            </CardTitle>
            <XCircle className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {vacationAlerts.filter(a => a.alert_type === 'vencido').length}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Funcionários com férias vencidas
            </p>
          </CardContent>
        </Card>

        <Card className="dashboard-card card-hover">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Férias a Vencer (30 dias)
            </CardTitle>
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {vacationAlerts.filter(a => a.alert_type === 'vencimento_proximo' && a.days_until_expiry <= 30).length}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Próximos 30 dias
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Company breakdown */}
      {dashboardData.totalByCompany.length > 0 && (
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5 text-slate-600" />
              <span>Total por Empresa</span>
            </CardTitle>
            <CardDescription>
              Distribuição da folha de pagamento por empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.totalByCompany.map((company, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-slate-50 to-blue-50 p-4 rounded-lg border border-slate-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        {company.company}
                      </p>
                      <p className="text-lg font-bold text-slate-800">
                        {formatCurrency(company.total)}
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-blue-500 opacity-70" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* HeadCount por Empresa */}
      {dashboardData.headCountData.length > 0 && (
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>HeadCount por Empresa</span>
            </CardTitle>
            <CardDescription>
              Análise de faturamento e produtividade por empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.headCountData.map((item, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-600">
                        {item.empresa}
                      </p>
                      <Building2 className="h-5 w-5 text-green-500 opacity-70" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-xs text-slate-500">Faturamento:</span>
                        <span className="text-sm font-medium text-slate-700">
                          {formatCurrency(item.faturamento)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-slate-500">Funcionários:</span>
                        <span className="text-sm font-medium text-slate-700">
                          {item.funcionarios}
                        </span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-green-200">
                        <span className="text-xs font-medium text-green-600">HeadCount:</span>
                        <span className="text-sm font-bold text-green-700">
                          {formatCurrency(item.headcount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total de Funcionários por Empresa */}
      {dashboardData.employeesByCompany.length > 0 && (
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Funcionários por Empresa</span>
            </CardTitle>
            <CardDescription>
              Total de funcionários ativos por empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.employeesByCompany.map((company, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        {company.empresa}
                      </p>
                      <p className="text-2xl font-bold text-blue-700">
                        {company.total_funcionarios}
                      </p>
                      <p className="text-xs text-slate-500">funcionários</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500 opacity-70" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* TurnOver */}
      {dashboardData.turnoverData.length > 0 && (
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>TurnOver</span>
            </CardTitle>
            <CardDescription>
              Índice de rotatividade mensal - [(Admissões + Demissões) / 2] / Total Funcionários Mês Anterior × 100
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.turnoverData.map((turnover, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    turnover.empresa === 'GERAL' 
                      ? 'bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200'
                      : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200'
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${
                        turnover.empresa === 'GERAL' ? 'text-purple-700' : 'text-orange-700'
                      }`}>
                        {turnover.empresa}
                      </p>
                      <TrendingUp className={`h-5 w-5 opacity-70 ${
                        turnover.empresa === 'GERAL' ? 'text-purple-500' : 'text-orange-500'
                      }`} />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Funcionários mês anterior:</span>
                        <span className="font-medium">{turnover.total_funcionarios_mes_anterior}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Admissões:</span>
                        <span className="font-medium text-green-600">+{turnover.admissoes_mes_atual}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Demissões:</span>
                        <span className="font-medium text-red-600">-{turnover.demissoes_mes_atual}</span>
                      </div>
                      <div className={`flex justify-between pt-2 border-t ${
                        turnover.empresa === 'GERAL' ? 'border-purple-200' : 'border-orange-200'
                      }`}>
                        <span className="text-xs font-medium text-slate-600">TurnOver:</span>
                        <span className={`text-lg font-bold ${
                          turnover.turnover_percentual > 10 
                            ? 'text-red-600' 
                            : turnover.turnover_percentual > 5 
                            ? 'text-yellow-600' 
                            : 'text-green-600'
                        }`}>
                          {turnover.turnover_percentual}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vacation Alerts and Birthday Employees */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vacation Alerts */}
        {vacationAlerts.length > 0 ? (
          <Card className="dashboard-card border-l-4 border-l-red-500">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <span>Alertas - Vencimento de Férias</span>
              </CardTitle>
              <CardDescription>
                Funcionários que precisam tirar férias urgentemente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {vacationAlerts.slice(0, 5).map((alert, index) => {
                  const isExpired = alert.alert_type === 'vencido';
                  const isUrgent = alert.alert_type === 'vencimento_proximo';
                  
                  return (
                    <div 
                      key={alert.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isExpired 
                          ? 'bg-red-50 border-red-200' 
                          : isUrgent 
                          ? 'bg-yellow-50 border-yellow-200' 
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isExpired 
                            ? 'bg-red-100 text-red-600' 
                            : isUrgent 
                            ? 'bg-yellow-100 text-yellow-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {isExpired ? (
                            <XCircle className="h-5 w-5" />
                          ) : (
                            <Clock className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            {alert.employee_name}
                          </p>
                          <p className={`text-sm ${
                            isExpired 
                              ? 'text-red-600' 
                              : isUrgent 
                              ? 'text-yellow-600' 
                              : 'text-blue-600'
                          }`}>
                            {alert.message}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-600">
                          {alert.days_remaining} dias disponíveis
                        </p>
                        <p className="text-xs text-slate-500">
                          Vence em: {new Date(alert.concession_end).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  );
                })}
                
                {vacationAlerts.length > 0 && (
                  <div className="text-center pt-2">
                    {vacationAlerts.length > 5 && (
                      <p className="text-sm text-slate-500">
                        E mais {vacationAlerts.length - 5} funcionários com alertas de férias
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => navigate('/ferias')}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Ver Controle de Férias
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="dashboard-card border-l-4 border-l-gray-300">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-gray-600">
                <AlertTriangle className="h-5 w-5" />
                <span>Alertas de Férias</span>
              </CardTitle>
              <CardDescription>
                Não há alertas de férias pendentes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  Todas as férias estão em dia
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Birthday Employees */}
        <Card className="dashboard-card border-l-4 border-l-blue-500 flex flex-col h-full">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2 text-blue-700">
                  <Cake className="h-5 w-5" />
                  <span>Aniversários do Mês</span>
                </CardTitle>
                <CardDescription>
                  Funcionários que fazem aniversário em{' '}
                  {(() => {
                    const label = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                    return label.charAt(0).toUpperCase() + label.slice(1);
                  })()}
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2 shrink-0">
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/aniversarios">Ver todos</Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportBirthdaysCSV}
                  disabled={birthdayEmployees.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            {birthdaysLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="loading-spinner w-6 h-6 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
              </div>
            ) : birthdayEmployees.length > 0 ? (
              <div className="space-y-3 flex-1 overflow-y-auto pr-1 min-h-0">
                {birthdayEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-blue-50 border border-blue-200"
                  >
                    <div className="relative w-12 h-12 rounded-full overflow-hidden bg-blue-100">
                      {employee.foto ? (
                        <img
                          src={employee.foto}
                          alt={employee.nome}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gift className="h-6 w-6 text-blue-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">
                        {employee.nome}
                      </p>
                      <p className="text-sm text-blue-600">
                        {employee.birthday_date.toLocaleDateString('pt-BR', {
                          day: 'numeric',
                          month: 'long'
                        })} - {employee.age} anos
                      </p>
                    </div>
                    <div className="text-blue-500">
                      <Cake className="h-5 w-5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <Cake className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  Nenhum aniversário este mês
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default Dashboard;